// @ts-nocheck
import { useState } from 'react';
import {
  Stack,
  Title,
  Text as MantineText,
  Code,
  Divider,
  Button,
  Group,
} from '@mantine/core';
import { IconRocket } from '@tabler/icons-react';
import { Card, Alert } from '../../../components/ui';
import { toast, alert, banner } from '../../../components/ui/providers';

export default function AlertsPage() {
  const [showAnimatedAlert, setShowAnimatedAlert] = useState(false);

  // Handler for promise toast example
  const handlePromiseToast = () => {
    const promise = new Promise((resolve) => setTimeout(resolve, 2000));

    toast.promise(promise, {
      loading: 'Loading data...',
      success: 'Data loaded successfully!',
      error: 'Failed to load data',
    });
  };

  // Handler for confirm dialog
  const handleConfirm = async () => {
    const confirmed = await alert.confirm(
      'Delete Item?',
      'This action cannot be undone. Are you sure you want to proceed?',
      {
        confirmText: 'Delete',
        cancelText: 'Cancel',
      }
    );

    if (confirmed) {
      toast.success('Deleted!', 'Item has been deleted successfully');
    } else {
      toast.info('Cancelled', 'Deletion cancelled');
    }
  };

  // Handler for prompt dialog
  const handlePrompt = async () => {
    const name = await alert.prompt('Enter Your Name', 'Please provide your full name', {
      input: {
        placeholder: 'John Doe',
        defaultValue: '',
      },
    });

    if (name) {
      toast.success('Welcome!', `Hello, ${name}!`);
    }
  };

  // Handler for chained alerts
  const handleChainedAlerts = async () => {
    const confirmed = await alert.confirm(
      'Proceed with Action?',
      'This will perform an important operation.'
    );

    if (confirmed) {
      const promise = new Promise((resolve) => setTimeout(resolve, 1500));

      await toast.promise(promise, {
        loading: 'Processing...',
        success: 'Operation completed!',
        error: 'Operation failed',
      });

      await alert.success('Success!', 'The operation completed successfully');
    }
  };

  return (
    <Stack gap="xl">
      {/* Page Header */}
      <Stack gap="xs">
        <Title order={2}>Alert System</Title>
        <MantineText c="dimmed" size="sm">
          Comprehensive alert system with toast notifications, modal alerts, banners, and inline alerts
        </MantineText>
      </Stack>

      {/* Toast Notifications Section */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Toast Notifications</Title>
            <MantineText size="sm" c="dimmed">
              Toastr-style notifications that stack and auto-dismiss
            </MantineText>
          </Stack>

          {/* Basic Toasts */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Basic Types:
            </MantineText>
            <Group>
              <Button onClick={() => toast.success('Success!', 'Operation completed successfully')}>
                Success Toast
              </Button>
              <Button onClick={() => toast.error('Error!', 'Something went wrong')} color="red">
                Error Toast
              </Button>
              <Button onClick={() => toast.warning('Warning!', 'Please be careful')} color="yellow">
                Warning Toast
              </Button>
              <Button onClick={() => toast.info('Info', 'This is informational')} color="blue">
                Info Toast
              </Button>
            </Group>
          </Stack>

          {/* Code Example */}
          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`import { toast } from '@/components/ui';

toast.success('Success!', 'Operation completed successfully');
toast.error('Error!', 'Something went wrong');
toast.warning('Warning!', 'Please be careful');
toast.info('Info', 'This is informational');`}
            </Code>
          </Stack>

          <Divider />

          {/* Gradient Toasts */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Gradient Toasts:
            </MantineText>
            <Group>
              <Button
                onClick={() =>
                  toast.success('Success!', 'With gradient background', {
                    gradient: true,
                    gradientVariant: 'success',
                  })
                }
              >
                Gradient Success
              </Button>
              <Button
                onClick={() =>
                  toast.info('Info', 'Beautiful gradient', {
                    gradient: true,
                    gradientVariant: 'primary',
                  })
                }
                color="blue"
              >
                Gradient Primary
              </Button>
              <Button
                onClick={() =>
                  toast.warning('Warning', 'With custom gradient', {
                    gradient: true,
                    gradientVariant: 'warning',
                  })
                }
                color="yellow"
              >
                Gradient Warning
              </Button>
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`toast.success('Success!', 'With gradient background', {
  gradient: true,
  gradientVariant: 'success',
});`}
            </Code>
          </Stack>

          <Divider />

          {/* Position Examples */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Positions:
            </MantineText>
            <Group>
              <Button onClick={() => toast.info('Top Right', '', { position: 'top-right' })} size="xs">
                Top Right
              </Button>
              <Button onClick={() => toast.info('Top Left', '', { position: 'top-left' })} size="xs">
                Top Left
              </Button>
              <Button onClick={() => toast.info('Top Center', '', { position: 'top-center' })} size="xs">
                Top Center
              </Button>
              <Button onClick={() => toast.info('Bottom Right', '', { position: 'bottom-right' })} size="xs">
                Bottom Right
              </Button>
              <Button onClick={() => toast.info('Bottom Left', '', { position: 'bottom-left' })} size="xs">
                Bottom Left
              </Button>
              <Button onClick={() => toast.info('Bottom Center', '', { position: 'bottom-center' })} size="xs">
                Bottom Center
              </Button>
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`toast.info('Top Right', '', { position: 'top-right' });
toast.info('Bottom Center', '', { position: 'bottom-center' });`}
            </Code>
          </Stack>

          <Divider />

          {/* With Action Button */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              With Action Button:
            </MantineText>
            <Button
              onClick={() =>
                toast.info('Update Available', 'A new version is available', {
                  action: {
                    label: 'Update',
                    onClick: () => alert.info('Updating', 'Starting update process...'),
                  },
                  duration: 0, // Don't auto-dismiss
                })
              }
            >
              Show Toast with Action
            </Button>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`toast.info('Update Available', 'A new version is available', {
  action: {
    label: 'Update',
    onClick: () => handleUpdate(),
  },
  duration: 0, // Don't auto-dismiss
});`}
            </Code>
          </Stack>

          <Divider />

          {/* Promise Toast */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Promise Toast:
            </MantineText>
            <Button onClick={handlePromiseToast}>Show Promise Toast</Button>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`const promise = new Promise((resolve) => setTimeout(resolve, 2000));

toast.promise(promise, {
  loading: 'Loading data...',
  success: 'Data loaded successfully!',
  error: 'Failed to load data',
});`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Modal Alerts Section */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Modal Alerts</Title>
            <MantineText size="sm" c="dimmed">
              SweetAlert-style modal dialogs with promise-based API
            </MantineText>
          </Stack>

          {/* Basic Modal Alerts */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Basic Types:
            </MantineText>
            <Group>
              <Button
                onClick={() => alert.success('Success!', 'Your changes have been saved successfully')}
              >
                Success Alert
              </Button>
              <Button onClick={() => alert.error('Error!', 'Unable to process your request')} color="red">
                Error Alert
              </Button>
              <Button
                onClick={() => alert.warning('Warning!', 'This action cannot be undone')}
                color="yellow"
              >
                Warning Alert
              </Button>
              <Button onClick={() => alert.info('Info', 'Here is some important information')} color="blue">
                Info Alert
              </Button>
            </Group>
          </Stack>

          {/* Code Example */}
          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`import { alert } from '@/components/ui';

await alert.success('Success!', 'Your changes have been saved');
await alert.error('Error!', 'Unable to process your request');
await alert.warning('Warning!', 'This action cannot be undone');
await alert.info('Info', 'Here is some information');`}
            </Code>
          </Stack>

          <Divider />

          {/* Confirm Dialog */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Confirm Dialog:
            </MantineText>
            <Button onClick={handleConfirm}>Show Confirm Dialog</Button>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`const confirmed = await alert.confirm(
  'Delete Item?',
  'This action cannot be undone. Are you sure?',
  {
    confirmText: 'Delete',
    cancelText: 'Cancel',
  }
);

if (confirmed) {
  // User clicked confirm
  toast.success('Deleted!', 'Item has been deleted');
}`}
            </Code>
          </Stack>

          <Divider />

          {/* Prompt Dialog */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Prompt Dialog:
            </MantineText>
            <Button onClick={handlePrompt}>Show Prompt Dialog</Button>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`const name = await alert.prompt(
  'Enter Your Name',
  'Please provide your full name',
  {
    input: {
      placeholder: 'John Doe',
      defaultValue: '',
    },
  }
);

if (name) {
  console.log('User entered:', name);
}`}
            </Code>
          </Stack>

          <Divider />

          {/* Gradient Alerts */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Gradient Alerts:
            </MantineText>
            <Group>
              <Button
                onClick={() =>
                  alert.success('Success!', 'With beautiful gradient', {
                    gradient: true,
                    gradientVariant: 'success',
                  })
                }
              >
                Gradient Success
              </Button>
              <Button
                onClick={() =>
                  alert.info('Premium Feature', 'Upgrade to unlock this feature', {
                    gradient: true,
                    gradientVariant: 'primary',
                  })
                }
                color="blue"
              >
                Gradient Primary
              </Button>
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`await alert.success('Success!', 'With beautiful gradient', {
  gradient: true,
  gradientVariant: 'success',
});`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Banner Alerts Section */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Banner Alerts</Title>
            <MantineText size="sm" c="dimmed">
              Full-width banner notifications at top or bottom of viewport
            </MantineText>
          </Stack>

          {/* Basic Banners */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Basic Types:
            </MantineText>
            <Group>
              <Button
                onClick={() => banner.success('Successfully saved!', { position: 'top', duration: 3000 })}
              >
                Top Success Banner
              </Button>
              <Button
                onClick={() => banner.error('An error occurred', { position: 'bottom', duration: 3000 })}
                color="red"
              >
                Bottom Error Banner
              </Button>
              <Button
                onClick={() =>
                  banner.info('New version available', {
                    position: 'top',
                    action: {
                      label: 'Update',
                      onClick: () => toast.info('Update', 'Starting update...'),
                    },
                    duration: 5000,
                  })
                }
                color="blue"
              >
                Banner with Action
              </Button>
            </Group>
          </Stack>

          {/* Code Example */}
          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`import { banner } from '@/components/ui';

banner.success('Successfully saved!', { position: 'top' });

banner.info('New version available', {
  position: 'top',
  action: {
    label: 'Update',
    onClick: () => handleUpdate(),
  },
  persistent: true,
});`}
            </Code>
          </Stack>

          <Divider />

          {/* Persistent Banner */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Persistent Banner:
            </MantineText>
            <Button
              onClick={() =>
                banner.warning('Maintenance scheduled for tomorrow at 2 AM', {
                  position: 'top',
                  persistent: true,
                  dismissible: true,
                  title: 'Scheduled Maintenance',
                })
              }
              color="yellow"
            >
              Show Persistent Banner
            </Button>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`banner.warning('Maintenance scheduled for tomorrow', {
  position: 'top',
  persistent: true, // Won't auto-dismiss
  dismissible: true, // Can be manually closed
  title: 'Scheduled Maintenance',
});`}
            </Code>
          </Stack>

          <Divider />

          {/* Gradient Banner */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Gradient Banner:
            </MantineText>
            <Button
              onClick={() =>
                banner.info('Special announcement!', {
                  position: 'top',
                  gradient: true,
                  gradientVariant: 'primary',
                  title: 'Announcement',
                  duration: 4000,
                })
              }
              variant="gradient"
            >
              Gradient Banner
            </Button>
          </Stack>
        </Stack>
      </Card>

      {/* Inline Alerts Section */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Inline Alerts</Title>
            <MantineText size="sm" c="dimmed">
              Contextual alerts embedded within content
            </MantineText>
          </Stack>

          {/* Basic Inline Alerts */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Basic Types:
            </MantineText>
            <Stack gap="sm">
              <Alert type="success" title="Success">
                Your changes have been saved successfully.
              </Alert>
              <Alert type="error" title="Error">
                There was an error processing your request.
              </Alert>
              <Alert type="warning" title="Warning">
                Please review the information before proceeding.
              </Alert>
              <Alert type="info" title="Information">
                This is an informational message.
              </Alert>
            </Stack>
          </Stack>

          {/* Code Example */}
          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`import { Alert } from '@/components/ui';

<Alert type="success" title="Success">
  Your changes have been saved successfully.
</Alert>

<Alert type="error" title="Error">
  There was an error processing your request.
</Alert>`}
            </Code>
          </Stack>

          <Divider />

          {/* Alert Variants */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Alert Variants:
            </MantineText>
            <Stack gap="sm">
              <Alert type="info" alertVariant="filled" title="Filled Variant">
                Filled style alert
              </Alert>
              <Alert type="success" alertVariant="outline" title="Outline Variant">
                Outline style alert
              </Alert>
              <Alert type="warning" alertVariant="gradient" gradientVariant="warning" title="Gradient Variant">
                Gradient style alert
              </Alert>
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`<Alert type="info" alertVariant="filled" title="Filled">
  Filled style alert
</Alert>

<Alert type="warning" alertVariant="gradient" gradientVariant="warning">
  Gradient style alert
</Alert>`}
            </Code>
          </Stack>

          <Divider />

          {/* Animated Inline Alerts */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              With Animation:
            </MantineText>
            <Button onClick={() => setShowAnimatedAlert(true)} size="sm">
              Show Animated Alert
            </Button>
            {showAnimatedAlert && (
              <Alert
                type="info"
                title="Animated Alert"
                withAnimation
                animationType="slide"
                withCloseButton
                onClose={() => setShowAnimatedAlert(false)}
              >
                This alert slides in smoothly with animation.
              </Alert>
            )}
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`<Alert
  type="info"
  title="Animated Alert"
  withAnimation
  animationType="slide"  // 'fade' | 'slide' | 'scale'
  withCloseButton
  onClose={() => setVisible(false)}
>
  This alert slides in smoothly.
</Alert>`}
            </Code>
          </Stack>

          <Divider />

          {/* Alert with Action */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              With Action Button:
            </MantineText>
            <Alert
              type="warning"
              title="Action Required"
              action={{
                label: 'Review',
                onClick: () => toast.info('Review', 'Opening review panel...'),
              }}
            >
              Please review the pending items in your dashboard.
            </Alert>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`<Alert
  type="warning"
  title="Action Required"
  action={{
    label: 'Review',
    onClick: () => handleReview(),
  }}
>
  Please review the pending items.
</Alert>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Advanced Examples Section */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Advanced Examples</Title>
            <MantineText size="sm" c="dimmed">
              Complex usage patterns and combinations
            </MantineText>
          </Stack>

          {/* Chained Alerts */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Chained Alerts:
            </MantineText>
            <Button onClick={handleChainedAlerts}>Show Chained Alerts</Button>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`const handleDelete = async () => {
  const confirmed = await alert.confirm(
    'Delete Item?',
    'This action cannot be undone.'
  );

  if (confirmed) {
    const promise = deleteItem();

    await toast.promise(promise, {
      loading: 'Deleting...',
      success: 'Deleted!',
      error: 'Failed to delete',
    });

    await alert.success('Complete', 'Item has been deleted');
  }
};`}
            </Code>
          </Stack>

          <Divider />

          {/* Custom Icons */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Custom Icons:
            </MantineText>
            <Button
              onClick={() =>
                toast.custom({
                  type: 'success',
                  title: 'Rocket Launch!',
                  message: 'Your project has been deployed',
                  icon: <IconRocket size={20} />,
                  position: 'top-right',
                })
              }
              leftSection={<IconRocket size={16} />}
            >
              Toast with Custom Icon
            </Button>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`import { IconRocket } from '@tabler/icons-react';

toast.custom({
  type: 'success',
  title: 'Rocket Launch!',
  message: 'Your project has been deployed',
  icon: <IconRocket size={20} />,
  position: 'top-right',
});`}
            </Code>
          </Stack>

          <Divider />

          {/* Dismiss Controls */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Dismiss Controls:
            </MantineText>
            <Group>
              <Button
                onClick={() => {
                  toast.info('Toast 1', 'This will stay');
                  toast.info('Toast 2', 'This too');
                  toast.info('Toast 3', 'And this');
                }}
                size="sm"
              >
                Show Multiple Toasts
              </Button>
              <Button onClick={() => toast.dismissAll()} size="sm" color="red">
                Dismiss All
              </Button>
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`// Dismiss specific toast
const id = toast.info('Message', 'Content');
toast.dismiss(id);

// Dismiss all toasts
toast.dismissAll();

// Dismiss banner
banner.dismiss();`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Advanced Features */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Advanced Features</Title>
            <MantineText size="sm" c="dimmed">
              Additional features including auto-close, backdrop, blur, and click-outside control
            </MantineText>
          </Stack>

          {/* Auto-close Modal */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Auto-Close Modal (3 seconds):
            </MantineText>
            <Button
              onClick={() =>
                alert.success('Auto-Close!', 'This alert will close automatically in 3 seconds', {
                  duration: 3000,
                })
              }
            >
              Show Auto-Close Alert
            </Button>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`alert.success('Auto-Close!', 'Closes in 3 seconds', {
  duration: 3000, // Auto-close after 3 seconds
});`}
            </Code>
          </Stack>

          <Divider />

          {/* Modal with disabled close on outside click */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Disabled Close on Outside Click:
            </MantineText>
            <Button
              onClick={() =>
                alert.warning(
                  'Important Action',
                  'You must choose an option. Cannot close by clicking outside',
                  {
                    closeOnClickOutside: false,
                    confirmText: 'I Understand',
                  }
                )
              }
              color="yellow"
            >
              Show Locked Alert
            </Button>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`alert.warning('Important Action', 'Must choose an option', {
  closeOnClickOutside: false, // Disable outside click
  confirmText: 'I Understand',
});`}
            </Code>
          </Stack>

          <Divider />

          {/* Modal with blur */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Modal with Blurred Backdrop:
            </MantineText>
            <Button
              onClick={() =>
                alert.info('Blurred Background', 'Notice the blurred backdrop effect', {
                  withBlur: true,
                })
              }
              color="blue"
            >
              Show Blurred Modal
            </Button>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`alert.info('Blurred Background', 'Notice the effect', {
  withBlur: true, // Enable backdrop blur
});`}
            </Code>
          </Stack>

          <Divider />

          {/* Banner with backdrop */}
          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Banner with Backdrop:
            </MantineText>
            <Group>
              <Button
                onClick={() =>
                  banner.warning('Important Notice with Backdrop', {
                    title: 'Notice',
                    position: 'top',
                    withBackdrop: true,
                    dismissible: true,
                  })
                }
                color="yellow"
              >
                Show Banner with Backdrop
              </Button>
              <Button
                onClick={() =>
                  banner.error('Critical Alert with Blurred Backdrop', {
                    title: 'Critical',
                    position: 'top',
                    withBackdrop: true,
                    withBlur: true,
                    dismissible: true,
                  })
                }
                color="red"
              >
                Banner with Blur
              </Button>
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
              {`// With backdrop
banner.warning('Important Notice', {
  withBackdrop: true, // Show overlay
});

// With blurred backdrop
banner.error('Critical Alert', {
  withBackdrop: true,
  withBlur: true, // Blur the backdrop
});`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* API Reference */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>API Reference</Title>
            <MantineText size="sm" c="dimmed">
              Quick reference for all alert APIs
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Toast API:
            </MantineText>
            <Code block language="tsx">
              {`toast.success(title, message?, options?) => string
toast.error(title, message?, options?) => string
toast.warning(title, message?, options?) => string
toast.info(title, message?, options?) => string
toast.custom(options) => string
toast.promise(promise, options) => Promise
toast.dismiss(id) => void
toast.dismissAll() => void`}
            </Code>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Alert API (Modal):
            </MantineText>
            <Code block language="tsx">
              {`alert.success(title, message, options?) => Promise<void>
alert.error(title, message, options?) => Promise<void>
alert.warning(title, message, options?) => Promise<void>
alert.info(title, message, options?) => Promise<void>
alert.confirm(title, message, options?) => Promise<boolean>
alert.prompt(title, message, options?) => Promise<string | null>`}
            </Code>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Banner API:
            </MantineText>
            <Code block language="tsx">
              {`banner.success(message, options?) => string
banner.error(message, options?) => string
banner.warning(message, options?) => string
banner.info(message, options?) => string
banner.dismiss(id?) => void`}
            </Code>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
